import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Grid,
  Typography,
  Paper,
  InputAdornment,
  IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import axios from "axios";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";

export default function StudentProfile() {
  const [searchRollno, setSearchRollno] = useState("");
  const [form, setForm] = useState({
    student_rollno: "",
    student_name: "",
    admission_no: "",
    dob: "",
    gender: "",
    email: "",
    mobile: "",
    address: "",
    photo_url: "",
    second_language: "TELUGU",
    student_status: "",
  });
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  // Snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const user = JSON.parse(sessionStorage.getItem("user"));
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const collegeId = user?.college_id;
  const clientId = user?.client_id;

  // Fetch student by roll number
  const fetchStudent = async (rollno) => {
    if (!rollno) return;
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/api/students/rollno/${clientId}/${collegeId}/${rollno}`
      );
      console.log(data);
      if (data.success && data.student) {
        const s = data.student;

        setForm({
          student_rollno: s.student_rollno,
          student_name: s.student_name,
          admission_no: s.admission_no,
          dob: s.dob ? new Date(s.dob).toISOString().substring(0, 10) : "",
          gender: s.gender,
          email: s.email,
          mobile: s.mobile ? s.mobile.replace(/\n/g, " ") : "",
          address: s.address || "",
          photo_url: s.photo_url || "",
          second_language: s.second_language || "TELUGU",
          student_status: s.student_status,
        });
        setEditing(s);
        setOpen(true);
      } else {
        showSnackbar("Student not found", "error");
      }
    } catch (err) {
      console.error(err);
      showSnackbar(err?.response?.data?.message || "Failed to fetch student", "error");
    } finally {
      setLoading(false);
    }
  };

  // Submit update
  const handleSubmit = async () => {
    if (!editing) return;
    const payload = {
      ...form,
      college_id: Number(collegeId),
      client_id: Number(clientId),
    };
    try {
      await axios.put(
        `${API_BASE_URL}/api/students/update/${clientId}/${collegeId}/${editing.student_id}`,
        payload
      );
      showSnackbar("Student updated successfully", "success");
      setOpen(false);
    } catch (err) {
      console.error(err);
      showSnackbar(err?.response?.data?.message || "Update failed", "error");
    }
  };

  // Upload photo
  const handlePhotoUpload = async () => {
    if (!selectedPhoto) {
      showSnackbar("Please select a photo first", "error");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("photo", selectedPhoto);
      const admittedbatchName = editing.admittedbatch?.admittedbatch || "";

      const branchName = (editing.branch?.branch_name || "").replace(/\s+/g, "");

      const { data } = await axios.post(
        `${API_BASE_URL}/api/students/upload-photo?college_id=${collegeId}&admittedbatch=${admittedbatchName}&branchname=${encodeURIComponent(
          branchName
        )}&student_rollno=${form.student_rollno}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (data.success) {
        setForm({ ...form, photo_url: data.photo_url });
        showSnackbar("Photo uploaded successfully", "success");
      } else {
        showSnackbar("Photo upload failed", "error");
      }
    } catch (err) {
      console.error(err);
      showSnackbar("Photo upload failed", "error");
    }
  };

  return (
    <Box p={2}>
      <Paper elevation={1} sx={{ p: 2 }}>
        <Typography variant="h6" mb={2}>
          UPDATE STUDENT PROFILE
        </Typography>
        <Stack direction="row" spacing={2}>
          <TextField
            size="small"
            placeholder="Enter Student Roll No"
            value={searchRollno}
            onChange={(e) => setSearchRollno(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter") fetchStudent(searchRollno);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => fetchStudent(searchRollno)}>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            fullWidth
            sx={{ width: { xs: "100%", sm: "50%", md: "50%" } }}
          />
        </Stack>
      </Paper>

      {/* Dialog for Edit */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Student</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12} sx={{ textAlign: "center" }}>
              {form.photo_url ? (
                <img
                  src={
                    selectedPhoto
                      ? URL.createObjectURL(selectedPhoto) // preview selected photo
                      : form.photo_url // existing uploaded photo
                  }
                  alt="Student"
                  style={{
                    width: 120,
                    height: 120,
                    borderRadius: "50%",
                    objectFit: "cover",
                    border: "2px solid #ccc",
                  }}
                />
              ) : (
                <div
                  style={{
                    width: 120,
                    height: 120,
                    borderRadius: "50%",
                    background: "#eee",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 14,
                    color: "#777",
                  }}
                >
                  No Photo
                </div>
              )}

              {/* Upload Photo Buttons */}
              <Stack direction="row" spacing={1} justifyContent="center" mt={1}>
                <Button
                  variant="outlined"
                  component="label"
                  size="small"
                  sx={{
                    color: "#1976d2",
                    borderColor: "#1976d2",
                    textTransform: "none",
                    "&:hover": { backgroundColor: "#e3f2fd", borderColor: "#1976d2" },
                  }}
                >
                  Select Photo
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={(e) => setSelectedPhoto(e.target.files[0])}
                  />
                </Button>

                <Button
                  variant="outlined"
                  size="small"
                  onClick={handlePhotoUpload}
                  disabled={!selectedPhoto}
                  sx={{
                    color: "#2e7d32",
                    borderColor: "#2e7d32",
                    textTransform: "none",
                    "&:hover": { backgroundColor: "#e8f5e9", borderColor: "#2e7d32" },
                  }}
                >
                  Upload Photo
                </Button>
              </Stack>
            </Grid>

            {/* Rest of the fields */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Roll No"
                disabled
                value={form.student_rollno}
                onChange={(e) => setForm({ ...form, student_rollno: e.target.value })}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Student Name"
                value={form.student_name}
                onChange={(e) => setForm({ ...form, student_name: e.target.value })}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Admission No"
                value={form.admission_no}
                onChange={(e) => setForm({ ...form, admission_no: e.target.value })}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="DOB"
                type="date"
                value={form.dob}
                onChange={(e) => setForm({ ...form, dob: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Gender"
                value={form.gender}
                onChange={(e) => setForm({ ...form, gender: e.target.value })}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Mobile"
                value={form.mobile}
                onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Student Status"
                select
                fullWidth
                value={form.student_status || "ACTIVE"}
                onChange={(e) => setForm({ ...form, student_status: e.target.value })}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: true }}
              >
                <option value="ACTIVE">ACTIVE</option>
                <option value="INACTIVE">INACTIVE</option>
                <option value="TCOUT">TCOUT</option>
                <option value="DETAINED">DETAINED</option>
                <option value="GRADUATED">GRADUATED</option>
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Address"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                fullWidth
                multiline
                maxRows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#1976d2",
              color: "#fff",
              textTransform: "none",
              "&:hover": { backgroundColor: "#115293" },
            }}
            onClick={handleSubmit}
          >
            Update Data
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MuiAlert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}
